/**
 * "Your workspace is paused" — one signal for every API client.
 *
 * When the billing lifecycle restricts an organization (an unpaid bill past
 * its grace), every WeCo service answers its members `403 {code:
 * "workspace_paused"}`. That is not a permission error a module can explain or
 * retry: the session itself is no longer usable for anything but billing.
 *
 * The API clients (`SuiteApiClient`, `fetchWithAuth`, the shell's own
 * client) report it here instead of each module deciding what to do. The
 * shell listens once and either moves an Owner/Admin to the billing-only
 * screen or signs a member out with an explanation.
 */

export const WORKSPACE_PAUSED_CODE = "workspace_paused";
export const WORKSPACE_PAUSED_EVENT = "weco:workspace-paused";

export interface WorkspacePausedEventDetail {
  /** The server's own sentence, when it sent one. */
  detail?: string;
}

/** True for a parsed error body that says the workspace is paused. */
export function isWorkspacePausedBody(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const b = body as { code?: unknown; errors?: Array<{ code?: unknown }> };
  if (b.code === WORKSPACE_PAUSED_CODE) return true;
  // drf-standardized-errors wraps codes in `errors[]`.
  return Array.isArray(b.errors) && b.errors.some((e) => e?.code === WORKSPACE_PAUSED_CODE);
}

/** Tell the shell. Safe to call outside a browser (it does nothing). */
export function reportWorkspacePaused(detail?: string): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(
    new CustomEvent<WorkspacePausedEventDetail>(WORKSPACE_PAUSED_EVENT, { detail: { detail } }),
  );
}

/** Report a 403 response if its body says the workspace is paused. Never throws. */
export async function reportIfWorkspacePaused(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  try {
    const body = await response.clone().json();
    if (!isWorkspacePausedBody(body)) return false;
    const detail = (body as { detail?: unknown }).detail;
    reportWorkspacePaused(typeof detail === "string" ? detail : undefined);
    return true;
  } catch {
    return false;
  }
}

/** Subscribe; returns the unsubscribe. */
export function onWorkspacePaused(listener: (detail: WorkspacePausedEventDetail) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (event: Event) =>
    listener(((event as CustomEvent<WorkspacePausedEventDetail>).detail ?? {}) as WorkspacePausedEventDetail);
  window.addEventListener(WORKSPACE_PAUSED_EVENT, handler);
  return () => window.removeEventListener(WORKSPACE_PAUSED_EVENT, handler);
}
